import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getCloudFrontImageUrl } from '../../util/cloudFrontUtils';

import { NavBarContext } from '../../App';
import NavBar from '../../components/NavBar/NavBar';
import NavBarPhone from '../../components/NavBarPhone/NavBarPhone';
import { SmallModal } from '../../components/UI/SmallModal/SmallModal';
import SaveNotification from '../../components/UI/SaveNotification/SaveNotification';
import { useFetchWishlists } from '../../hooks/wishlists/useFetchWishlists';
import { useCreateWishlist } from '../../hooks/wishlists/useCreateWishlist';
import { useDeleteWishlist } from '../../hooks/wishlists/useDeleteWishlist';
import { useUpdateWishlist } from '../../hooks/wishlists/useUpdateWishlist';
import { useViewportHeight } from '../../hooks/useViewportHeight';

import classes from "./FavouritePage.module.css";

export const FavouritePage = () => {
    const { activeView, setActiveView } = useContext(NavBarContext);
    const { id: userID } = useSelector((state) => state.auth.user);
    const [open, setOpen] = useState(false);
    const [isNewListOpen, setIsNewListOpen] = useState(false);
    const [newWishlistName, setNewWishlistName] = useState('');
    const [selectedWishlist, setSelectedWishlist] = useState(null);
    
    // Initialize viewport height fix for iOS Safari
    useViewportHeight();
    
    const { handleWishlistCreation } = useCreateWishlist();
    const { handleWishlistUpdate } = useUpdateWishlist();
    const { handleWishlistDeletion } = useDeleteWishlist();

    const { wishlistsData, isLoading, error } = useFetchWishlists(userID);

    const wishlists = wishlistsData;

    const [modalType, setModalType] = useState(null);
    const [showSaveNotification, setShowSaveNotification] = useState(false);
    const [saveNotificationMessage, setSaveNotificationMessage] = useState('Änderungen gespeichert');
    const navigate = useNavigate();
    const location = useLocation();

    const showNotification = useCallback((message) => {
        setSaveNotificationMessage(message);
        setShowSaveNotification(true);
    }, []);

    const hideSaveNotification = useCallback(() => {
        setShowSaveNotification(false);
    }, []);

    useEffect(() => {
        if (location.state?.wishlistNotification) {
            showNotification(location.state.wishlistNotification);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname, showNotification]);

    const handleWishlistClick = (id) => {
        navigate(`/wishlists/${id}`)
    };

    const handleNewListClick = () => {
        setIsNewListOpen(true);
    };

    const handleNewListClose = () => {
        setIsNewListOpen(false);
    };

    const processStudioImage = (imageUrl) => {
        if (!imageUrl) return '';
        return getCloudFrontImageUrl(imageUrl, {
            width: 1200,
            height: 800,
            fit: 'cover'
        });
    };

    const handleNew = () => {
        setModalType('new');
        setOpen(true);
    };
    
    const handleEdit = (wishlist) => {
        if (!wishlist || wishlist.name === 'Favoriten') return; // Prevent editing Favoriten wishlist
        setSelectedWishlist(wishlist);
        setNewWishlistName(wishlist.name || '');
        setModalType('edit');
        setOpen(true);
    };
    
    const handleClose = () => {
        setOpen(false);
        setModalType(null);
        setNewWishlistName('');
        setSelectedWishlist(null);
    };

    const handleDeleteWishlist = async () => {
        try {
            if (!selectedWishlist || selectedWishlist.name === 'Favoriten') {
                return; // Prevent deleting Favoriten wishlist
            }

            await handleWishlistDeletion(selectedWishlist.id);

            setSelectedWishlist(null);
            handleClose();
            showNotification('Kollektion gelöscht');
            
        } catch (error) {
            console.error('Error deleting wishlist:', error);
        }
    };

    const handleCreatingNewWishlist = async () => {
        try {
            if (!newWishlistName.trim()) {
                return;
            }

            await handleWishlistCreation({
                name: newWishlistName,
                userID: userID
            });

            setNewWishlistName('');
            handleClose();
            handleNewListClose();
            showNotification('Kollektion erstellt');

        } catch (error) {
            console.error('Error creating wishlist:', error);
        }
    };

    const handleEditWishlist = async () => {
        try {
            if (!selectedWishlist || !newWishlistName.trim()) {
                return;
            }

            await handleWishlistUpdate({
                id: selectedWishlist.id,
                name: newWishlistName
            });

            setSelectedWishlist(null);
            setNewWishlistName('');
            handleClose();
            showNotification('Änderungen gespeichert');
            
        } catch (error) {
            console.error('Error updating wishlist:', error);
        }
    };
  
    if (error) return <div>Favoritenlisten konnten nicht geladen werden.</div>;
  
  return (
      <div className={classes.pageContainer}>
        <div className={classes.navBarContainer}>
            <NavBar fromBookings={true} activeView={activeView}/>
        </div>
        <header className={classes.phoneHeader}>
            <Link to="/">
                <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
                    <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
                    <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
                    <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
                </svg>
            </Link>
        </header>
        <h1 className='titleSemibold2'>Wunschliste</h1>
        <div className={classes.longHorizontalLine}></div>
        <div className={classes.favouriteFoldersContainer}>
            {isLoading ? (
                <div className={classes.loadingContainer}>
                    <div className={classes.loadingSpinner}></div>
                </div>
            ) : (
                wishlists?.items?.map((wishlist, index) => (
                    <div 
                        onClick={() => handleWishlistClick(wishlist?.id, wishlist)} 
                        key={wishlist?.id} 
                        className={classes.favouriteFolder}
                    >
                        <div 
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(wishlist);
                            }}
                            className={`${classes.editIconContainer} ${wishlist?.name === 'Favoriten' ? classes.hidden : ''}`}
                        >
                        <svg xmlns="http://www.w3.org/2000/svg" width="2rem" height="2rem" viewBox="0 0 32 32" fill="none">
                            <circle cx="16" cy="16" r="16" fill="#F0F1F4"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M18.7677 9.21713C18.9864 8.99841 19.318 8.9385 19.5994 9.06684C20.2073 9.34403 20.8619 9.83094 21.4315 10.3609C22.0042 10.8937 22.5426 11.5182 22.8952 12.1092C23.0692 12.4007 23.0229 12.7731 22.7829 13.0132L20.9929 14.8023C20.9725 14.8236 20.9518 14.8442 20.9308 14.8643L19.424 16.3704C19.0956 16.6989 18.7671 17.0275 18.4385 17.3561C16.7297 19.0653 15.0195 20.776 13.3133 22.4809C13.1855 22.6087 13.0145 22.6862 12.8341 22.6973L9.78691 22.8854C9.57476 22.8985 9.3672 22.8199 9.21694 22.6695C9.06669 22.5192 8.98821 22.3116 9.00144 22.0994L9.18808 19.106C9.18634 18.9141 9.25869 18.7216 9.40515 18.5751L10.6047 17.3756C10.6221 17.3581 10.6402 17.3417 10.6589 17.3264L16.9703 11.015C16.9752 11.0098 16.9801 11.0048 16.9851 10.9998C17.473 10.5105 17.9202 10.0639 18.3021 9.68235C18.4707 9.51396 18.6266 9.35824 18.7677 9.21713ZM11.5941 18.4828L10.6537 19.4232L10.5333 21.354L12.4627 21.2349L17.9822 15.7155C17.9821 15.7156 17.9823 15.7153 17.9822 15.7155L18.5111 15.1866C18.1338 15.0238 17.7863 14.7861 17.4943 14.5157C17.1637 14.2097 16.8695 13.8296 16.6753 13.4078L16.067 14.0167C13.9474 16.1345 13.7755 16.3064 11.6577 18.426C11.6375 18.4463 11.6162 18.4652 11.5941 18.4828ZM18.0429 12.0389C17.923 12.1752 17.8843 12.3388 17.9448 12.5742C18.0134 12.8412 18.2079 13.1562 18.5014 13.4279C18.7949 13.6996 19.1239 13.8693 19.3951 13.9171C19.6149 13.9558 19.768 13.9157 19.8898 13.8079C19.9101 13.7875 19.9305 13.7672 19.9508 13.7469C19.9514 13.7463 19.952 13.7457 19.9526 13.745C19.9601 13.7376 19.9677 13.7303 19.9754 13.7232L21.2993 12.4C21.0566 12.0855 20.7536 11.7551 20.4217 11.4463C20.0828 11.131 19.7369 10.8596 19.4224 10.6587C19.3981 10.683 19.3735 10.7076 19.3486 10.7324C18.9718 11.1088 18.5329 11.5472 18.0531 12.0283C18.0497 12.0319 18.0463 12.0354 18.0429 12.0389Z" fill="black"/>
                        </svg>
                    </div>
                    <div className={classes.favouriteFolderImages}>
                        {!wishlist?.wishListStudios?.items?.length ? (
                            <div className={classes.emptyContainer}>
                                <svg className={classes.svg} xmlns="http://www.w3.org/2000/svg" width="188" height="88" viewBox="0 0 188 88" fill="none">
                                    <path d="M186.199 24.1462L178.709 79.5362C178.289 82.5862 175.489 84.7262 172.439 84.3062L152.049 81.5462C152.059 81.4062 152.069 81.2762 152.069 81.1362V13.9062L181.419 17.8762C184.469 18.2862 186.609 21.0962 186.199 24.1462Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                    <path d="M35.8195 81.5462L15.4295 84.3062C12.3795 84.7262 9.57947 82.5862 9.15947 79.5362L1.66947 24.1462C1.25947 21.0962 3.39947 18.2862 6.44947 17.8762L35.7995 13.9062V81.1362C35.7995 81.2762 35.8095 81.4062 35.8195 81.5462Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                    <path d="M152.069 6.86906V81.1391C152.069 81.2791 152.059 81.4091 152.049 81.5491C151.839 84.4291 149.439 86.7091 146.499 86.7091H41.3688C38.4288 86.7091 36.0288 84.4291 35.8188 81.5491C35.8088 81.4091 35.7988 81.2791 35.7988 81.1391V6.86906C35.7988 3.78906 38.2888 1.28906 41.3688 1.28906H146.499C149.579 1.28906 152.069 3.78906 152.069 6.86906Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                    <path d="M143.249 64.3984H44.0392C42.8849 64.3984 41.9492 65.3319 41.9492 66.4834C41.9492 67.635 42.8849 68.5685 44.0392 68.5685H143.249C144.403 68.5685 145.339 67.635 145.339 66.4834C145.339 65.3319 144.403 64.3984 143.249 64.3984Z" fill="#F0F1F4"/>
                                    <path d="M89.4092 72.3828H44.0392C42.8849 72.3828 41.9492 73.3163 41.9492 74.4678C41.9492 75.6193 42.8849 76.5528 44.0392 76.5528H89.4092C90.5635 76.5528 91.4992 75.6193 91.4992 74.4678C91.4992 73.3163 90.5635 72.3828 89.4092 72.3828Z" fill="#F0F1F4"/>
                                </svg>
                                <svg className={classes.svgMobile} xmlns="http://www.w3.org/2000/svg" width="166" height="122" viewBox="0 0 166 122" fill="none">
                                    <rect width="165.965" height="121.708" rx="12" fill="#F0F1F4"/>
                                    <path d="M117.058 45.5919L111.607 85.9019C111.297 88.1219 109.268 89.6819 107.048 89.3719L99.4375 88.4019C99.4375 88.3019 99.4475 88.2019 99.4475 88.1019V39.1719L113.577 41.0219C115.797 41.3219 117.358 43.3619 117.058 45.5819V45.5919Z" fill="white"/>
                                    <path d="M48.0369 45.5919L53.4869 85.9019C53.7969 88.1219 55.8269 89.6819 58.0469 89.3719L65.6569 88.4019C65.6569 88.3019 65.6469 88.2019 65.6469 88.1019V39.1719L51.5169 41.0219C49.2969 41.3219 47.7369 43.3619 48.0369 45.5819V45.5919Z" fill="white"/>
                                    <path d="M95.3965 29C98.1917 29.0001 100.446 31.2708 100.446 34.0596V88.1201C100.446 88.212 100.447 88.3618 100.432 88.5195L100.431 88.5186C100.23 91.124 98.0565 93.1796 95.3965 93.1797H69.7061C67.0367 93.1796 64.856 91.1096 64.6689 88.4912L64.666 88.4561V88.4199C64.666 88.3991 64.6648 88.3742 64.6621 88.3203C64.6598 88.2745 64.6563 88.1992 64.6562 88.1201V34.0596C64.6565 31.2708 66.9108 29.0001 69.7061 29H95.3965Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                    <path d="M93.9064 76.1719H70.2964C69.479 76.1719 68.8164 76.8345 68.8164 77.6519C68.8164 78.4693 69.479 79.1319 70.2964 79.1319H93.9064C94.7238 79.1319 95.3864 78.4693 95.3864 77.6519C95.3864 76.8345 94.7238 76.1719 93.9064 76.1719Z" fill="#F0F1F4"/>
                                    <path d="M80.0664 81.8203H70.2964C69.479 81.8203 68.8164 82.4829 68.8164 83.3003C68.8164 84.1177 69.479 84.7803 70.2964 84.7803H80.0664C80.8838 84.7803 81.5464 84.1177 81.5464 83.3003C81.5464 82.4829 80.8838 81.8203 80.0664 81.8203Z" fill="#F0F1F4"/>
                                </svg>
                            </div>
                        ) : 
                        wishlist?.wishListStudios?.items?.length === 1 || wishlist?.wishListStudios?.items?.length === 2 ? (
                            <div className={classes.singleImageContainer}>
                                {wishlist?.wishListStudios?.items[0]?.studio?.images?.[0] ? (
                                    <img 
                                        src={processStudioImage(wishlist.wishListStudios.items[0].studio.images[0])} 
                                        alt={wishlist.wishListStudios.items[0].studio.name || 'Studio'}  
                                    />
                                ) : (
                                    <div className={classes.emptyContainer}>
                                        <svg className={classes.svg} xmlns="http://www.w3.org/2000/svg" width="188" height="88" viewBox="0 0 188 88" fill="none">
                                            <path d="M186.199 24.1462L178.709 79.5362C178.289 82.5862 175.489 84.7262 172.439 84.3062L152.049 81.5462C152.059 81.4062 152.069 81.2762 152.069 81.1362V13.9062L181.419 17.8762C184.469 18.2862 186.609 21.0962 186.199 24.1462Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                            <path d="M35.8195 81.5462L15.4295 84.3062C12.3795 84.7262 9.57947 82.5862 9.15947 79.5362L1.66947 24.1462C1.25947 21.0962 3.39947 18.2862 6.44947 17.8762L35.7995 13.9062V81.1362C35.7995 81.2762 35.8095 81.4062 35.8195 81.5462Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                            <path d="M152.069 6.86906V81.1391C152.069 81.2791 152.059 81.4091 152.049 81.5491C151.839 84.4291 149.439 86.7091 146.499 86.7091H41.3688C38.4288 86.7091 36.0288 84.4291 35.8188 81.5491C35.8088 81.4091 35.7988 81.2791 35.7988 81.1391V6.86906C35.7988 3.78906 38.2888 1.28906 41.3688 1.28906H146.499C149.579 1.28906 152.069 3.78906 152.069 6.86906Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                            <path d="M143.249 64.3984H44.0392C42.8849 64.3984 41.9492 65.3319 41.9492 66.4834C41.9492 67.635 42.8849 68.5685 44.0392 68.5685H143.249C144.403 68.5685 145.339 67.635 145.339 66.4834C145.339 65.3319 144.403 64.3984 143.249 64.3984Z" fill="#F0F1F4"/>
                                            <path d="M89.4092 72.3828H44.0392C42.8849 72.3828 41.9492 73.3163 41.9492 74.4678C41.9492 75.6193 42.8849 76.5528 44.0392 76.5528H89.4092C90.5635 76.5528 91.4992 75.6193 91.4992 74.4678C91.4992 73.3163 90.5635 72.3828 89.4092 72.3828Z" fill="#F0F1F4"/>
                                        </svg>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className={classes.leftContainer}>
                                    {wishlist?.wishListStudios?.items[0]?.studio?.images?.[0] ? (
                                        <img 
                                            src={processStudioImage(wishlist.wishListStudios.items[0].studio.images[0])} 
                                            alt={wishlist.wishListStudios.items[0].studio.name || 'Studio'} 
                                        />
                                    ) : (
                                        <div className={classes.emptyContainer}>
                                            <svg className={classes.svg} xmlns="http://www.w3.org/2000/svg" width="188" height="88" viewBox="0 0 188 88" fill="none">
                                                <path d="M186.199 24.1462L178.709 79.5362C178.289 82.5862 175.489 84.7262 172.439 84.3062L152.049 81.5462C152.059 81.4062 152.069 81.2762 152.069 81.1362V13.9062L181.419 17.8762C184.469 18.2862 186.609 21.0962 186.199 24.1462Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                                <path d="M35.8195 81.5462L15.4295 84.3062C12.3795 84.7262 9.57947 82.5862 9.15947 79.5362L1.66947 24.1462C1.25947 21.0962 3.39947 18.2862 6.44947 17.8762L35.7995 13.9062V81.1362C35.7995 81.2762 35.8095 81.4062 35.8195 81.5462Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                                <path d="M152.069 6.86906V81.1391C152.069 81.2791 152.059 81.4091 152.049 81.5491C151.839 84.4291 149.439 86.7091 146.499 86.7091H41.3688C38.4288 86.7091 36.0288 84.4291 35.8188 81.5491C35.8088 81.4091 35.7988 81.2791 35.7988 81.1391V6.86906C35.7988 3.78906 38.2888 1.28906 41.3688 1.28906H146.499C149.579 1.28906 152.069 3.78906 152.069 6.86906Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                                <path d="M143.249 64.3984H44.0392C42.8849 64.3984 41.9492 65.3319 41.9492 66.4834C41.9492 67.635 42.8849 68.5685 44.0392 68.5685H143.249C144.403 68.5685 145.339 67.635 145.339 66.4834C145.339 65.3319 144.403 64.3984 143.249 64.3984Z" fill="#F0F1F4"/>
                                                <path d="M89.4092 72.3828H44.0392C42.8849 72.3828 41.9492 73.3163 41.9492 74.4678C41.9492 75.6193 42.8849 76.5528 44.0392 76.5528H89.4092C90.5635 76.5528 91.4992 75.6193 91.4992 74.4678C91.4992 73.3163 90.5635 72.3828 89.4092 72.3828Z" fill="#F0F1F4"/>
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <div className={classes.rightContainer}>
                                    <div className={classes.topContainer}>
                                        {wishlist?.wishListStudios?.items[1]?.studio?.images?.[0] ? (
                                            <img 
                                                src={processStudioImage(wishlist.wishListStudios.items[1].studio.images[0])} 
                                                alt={wishlist.wishListStudios.items[1].studio.name || 'Studio'} 
                                            />
                                        ) : (
                                            <div className={classes.emptyContainer}>
                                                <svg className={classes.svg} xmlns="http://www.w3.org/2000/svg" width="188" height="88" viewBox="0 0 188 88" fill="none">
                                                    <path d="M186.199 24.1462L178.709 79.5362C178.289 82.5862 175.489 84.7262 172.439 84.3062L152.049 81.5462C152.059 81.4062 152.069 81.2762 152.069 81.1362V13.9062L181.419 17.8762C184.469 18.2862 186.609 21.0962 186.199 24.1462Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                                    <path d="M35.8195 81.5462L15.4295 84.3062C12.3795 84.7262 9.57947 82.5862 9.15947 79.5362L1.66947 24.1462C1.25947 21.0962 3.39947 18.2862 6.44947 17.8762L35.7995 13.9062V81.1362C35.7995 81.2762 35.8095 81.4062 35.8195 81.5462Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                                    <path d="M152.069 6.86906V81.1391C152.069 81.2791 152.059 81.4091 152.049 81.5491C151.839 84.4291 149.439 86.7091 146.499 86.7091H41.3688C38.4288 86.7091 36.0288 84.4291 35.8188 81.5491C35.8088 81.4091 35.7988 81.2791 35.7988 81.1391V6.86906C35.7988 3.78906 38.2888 1.28906 41.3688 1.28906H146.499C149.579 1.28906 152.069 3.78906 152.069 6.86906Z" fill="white" stroke="#F0F1F4" strokeWidth="2"/>
                                                    <path d="M143.249 64.3984H44.0392C42.8849 64.3984 41.9492 65.3319 41.9492 66.4834C41.9492 67.635 42.8849 68.5685 44.0392 68.5685H143.249C144.403 68.5685 145.339 67.635 145.339 66.4834C145.339 65.3319 144.403 64.3984 143.249 64.3984Z" fill="#F0F1F4"/>
                                                    <path d="M89.4092 72.3828H44.0392C42.8849 72.3828 41.9492 73.3163 41.9492 74.4678C41.9492 75.6193 42.8849 76.5528 44.0392 76.5528H89.4092C90.5635 76.5528 91.4992 75.6193 91.4992 74.4678C91.4992 73.3163 90.5635 72.3828 89.4092 72.3828Z" fill="#F0F1F4"/>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    <div className={classes.bottomContainer}>
                                        {wishlist?.wishListStudios?.items?.length >= 3 && wishlist?.wishListStudios?.items[2]?.studio?.images?.[0] && (
                                            <img 
                                                src={processStudioImage(wishlist.wishListStudios.items[2].studio.images[0])} 
                                                alt={wishlist.wishListStudios.items[2].studio.name || 'Studio'} 
                                            />
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <h2 className='titleSemibold3'>{wishlist?.name}</h2>
                    <p className='regularSmall'>
                        {wishlist?.wishListStudios?.items?.length || 0} Studios
                    </p>
                </div>
                ))
            )}
            <div onClick={handleNew} className={`${classes.favouriteFolder} ${classes.addFolder}` }>
                <button className={classes.addButton}>
                    Neue Kollektion 
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" fill="#1F1F1F"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" fill="#1F1F1F"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" stroke="black" strokeWidth="0.1" strokeMiterlimit="10"/>
                    </svg>
                </button>
            </div>
        </div>
        <button onClick={handleNewListClick} className={classes.addButtonPhone}> 
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M17 9.5317H1V8.45539H17V9.5317Z" fill="#1F1F1F"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 1V17H8.45801V1H9.53433Z" fill="#1F1F1F"/>
            </svg>
            Neue Liste
        </button>
        <div className={classes.navBarPhoneContainer}>
            <NavBarPhone fromFavourite={true} activeView={activeView}/>
        </div>
        <SmallModal fromEditWishlist={true} open={open} onBackdropClick={handleClose} className={classes.smallModal}>
            {modalType === 'new' ? (
                <div className={classes.modalContent}>
                    <h2 className='titleSemibold3'>Neue Kollektion</h2>
                    <div onClick={handleClose} className={classes.closeButton}>
                        <svg className={classes.closeIcon} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                        </svg>
                    </div>
                    <div className={classes.modalInputContainer}>
                        <h3 className='semiboldSmall'>Name</h3>
                        <input 
                            type="text" 
                            placeholder='Name eingeben' 
                            className={`${classes.input} regularLarge`}
                            value={newWishlistName}
                            onChange={(e) => setNewWishlistName(e.target.value)}
                        />
                    </div>
                    <div className={classes.modalButtons}>
                        <button 
                            className={`${classes.modalButton} mediumLarge`} 
                            onClick={handleClose}
                            style={{border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                        >
                            Abbrechen
                        </button>
                        <button 
                            className={`${classes.modalButton2} mediumLarge`} 
                            onClick={handleCreatingNewWishlist}
                            style={{color: 'var(--white)', border: 'none', padding: '13px 21px'}}
                        >
                            Speichern
                        </button>
                    </div>
                </div>
            ) : (
                <div className={classes.modalContent}>
                    <h2 className='titleSemibold3'>Kollektion bearbeiten</h2>
                    <div onClick={handleClose} className={classes.closeButton}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                        </svg>
                    </div>
                    <div className={classes.modalInputContainer}>
                        <h3 className='semiboldSmall'>Name</h3>
                        <input 
                            style={{margin: '0'}} 
                            type="text" 
                            placeholder='Name eingeben' 
                            className={`${classes.input} regularLarge`}
                            value={newWishlistName}
                            onChange={(e) => setNewWishlistName(e.target.value)}
                        />
                    </div>
                    <button 
                        className={classes.deleteListButton}
                        onClick={handleDeleteWishlist}
                    >
                        Kollektion löschen
                    </button>
                    <div className={classes.modalButtons}>
                        <button 
                            className={`${classes.modalButton} mediumLarge`} 
                            onClick={() => {handleClose()}}
                            style={{border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                        >
                            Abbrechen
                        </button>
                        <button 
                            className={`${classes.modalButton2} mediumLarge`} 
                            onClick={handleEditWishlist}
                            style={{color: 'var(--white)', border: 'none', padding: '13px 21px'}}
                        >
                            Speichern
                        </button>
                    </div>
                </div>
            )}
        </SmallModal>
        <div 
            className={`${classes.overlay} ${isNewListOpen ? classes.overlayActive : ''}`}
            onClick={handleNewListClose}
        />
        <div className={`${classes.newListPhoneContainer} ${isNewListOpen ? classes.newListPhoneContainerActive : ''}`}>
            <div onClick={handleNewListClose} className={classes.closeButton}>
                <svg className={classes.closeIcon} xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M7.50947 8.5L0 0.990535L0.990535 0L8.5 7.50947L16.0095 0L17 0.990535L9.49054 8.5L17 16.0095L16.0095 17L8.5 9.49054L0.990535 17L0 16.0095L7.50947 8.5Z" fill="#1F1F1F"/>
                </svg>
            </div>
            <h2 className='titleSemibold3'>Neue Kollektion</h2>
            <div className={classes.horizontalLine}></div>
            <div className={classes.newListPhoneInputContainer}>
                <h3 className='semiboldSmall'>Name</h3>
                <input 
                    type="text" 
                    placeholder='Name eingeben' 
                    className={`${classes.input} regularLarge`}
                    value={newWishlistName}
                    onChange={(e) => setNewWishlistName(e.target.value)}
                />
            </div>
            <div className={classes.modalButtons}>
                <button 
                    className={`${classes.modalButton} mediumLarge`} 
                    onClick={() => {handleClose()}}
                    style={{border: '1px solid var(--light-grey-1)', color: 'var(--dark-grey)'}}
                >
                    Abbrechen
                </button>
                <button 
                    className={`${classes.modalButton2} mediumLarge`} 
                    onClick={handleCreatingNewWishlist}
                    style={{color: 'var(--white)', border: 'none', padding: '13px 21px'}}
                >
                    Speichern
                </button>
            </div>
        </div>
        <SaveNotification
            show={showSaveNotification}
            onHide={hideSaveNotification}
            message={saveNotificationMessage}
        />
      </div>
  );
};

export default FavouritePage;
